import { useState, useEffect, useCallback, useRef } from 'react'
import { useSearchParams } from 'react-router'
import { useAuth } from '@clerk/clerk-react'
import { format } from 'date-fns'
import { Search, AlertCircle, Play, Pause, Download, Loader2 } from 'lucide-react'
import { toast } from 'sonner'

import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import { useApiClient } from '@/shared/api'

interface CallLog {
  id: string
  timestamp: string
  event: string
  details: string
  campaignName?: string
  campaignId?: number
}

interface CallLogsData {
  phoneNumber: string | null
  callLogs: CallLog[]
  totalCalls: number
}

// Custom slider styles
const sliderStyles = `
  .slider::-webkit-slider-thumb {
    appearance: none;
    height: 14px;
    width: 14px;
    border-radius: 50%;
    background: #3b82f6;
    cursor: pointer;
    box-shadow: 0 0 2px 0 rgba(0, 0, 0, 0.2);
  }

  .slider::-moz-range-thumb {
    height: 14px;
    width: 14px;
    border-radius: 50%;
    background: #3b82f6;
    cursor: pointer;
    border: none;
    box-shadow: 0 0 2px 0 rgba(0, 0, 0, 0.2);
  }
`

// Global audio manager
const globalAudioManager = {
  currentAudioElement: null as HTMLAudioElement | null,
  currentAudioSetter: null as ((playing: boolean) => void) | null,
}

const stopCurrentAudio = () => {
  if (globalAudioManager.currentAudioElement) {
    globalAudioManager.currentAudioElement.pause()
    globalAudioManager.currentAudioElement.currentTime = 0
    if (globalAudioManager.currentAudioSetter) {
      globalAudioManager.currentAudioSetter(false)
    }
  }
}

// Audio player component
function AudioPlayer({ recordingName, campaignId }: { recordingName: string; campaignId: number }) {
  const { getToken } = useAuth()
  const [isPlaying, setIsPlaying] = useState(false)
  const [audioElement, setAudioElement] = useState<HTMLAudioElement | null>(null)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [isSeeking, setIsSeeking] = useState(false)
  const [wasPlaying, setWasPlaying] = useState(false)

  const handleSeekEnd = useCallback(() => {
    setIsSeeking(false)
    if (audioElement && wasPlaying) {
      audioElement.play()
    }
    setWasPlaying(false)
  }, [audioElement, wasPlaying])

  useEffect(() => {
    const handleGlobalMouseUp = () => {
      if (isSeeking) {
        handleSeekEnd()
      }
    }

    const handleGlobalTouchEnd = () => {
      if (isSeeking) {
        handleSeekEnd()
      }
    }

    if (isSeeking) {
      document.addEventListener('mouseup', handleGlobalMouseUp)
      document.addEventListener('touchend', handleGlobalTouchEnd)
    }

    return () => {
      document.removeEventListener('mouseup', handleGlobalMouseUp)
      document.removeEventListener('touchend', handleGlobalTouchEnd)
    }
  }, [isSeeking, handleSeekEnd])

  const handlePlayPause = async () => {
    if (!audioElement) {
      // Stop any currently playing audio
      stopCurrentAudio()

      setIsLoading(true)

      try {
        const token = await getToken()
        // Create new audio element using the API endpoint
        const audioUrl = `/api/v1/dialer/audio?filename=${encodeURIComponent(recordingName)}&campaign_id=${campaignId}`
        const audio = new Audio()

        // Set auth header via fetch and create blob URL
        const response = await fetch(audioUrl, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })

        if (!response.ok) {
          throw new Error('Failed to load audio')
        }

        const blob = await response.blob()
        audio.src = URL.createObjectURL(blob)

        // Add event listeners
        audio.addEventListener('ended', () => {
          setIsPlaying(false)
          setCurrentTime(0)
          if (globalAudioManager.currentAudioElement === audio) {
            globalAudioManager.currentAudioElement = null
            globalAudioManager.currentAudioSetter = null
          }
        })

        audio.addEventListener('error', () => {
          toast.error('Error playing audio')
          setIsPlaying(false)
          setIsLoading(false)
          if (globalAudioManager.currentAudioElement === audio) {
            globalAudioManager.currentAudioElement = null
            globalAudioManager.currentAudioSetter = null
          }
        })

        audio.addEventListener('loadedmetadata', () => {
          setDuration(audio.duration)
          setIsLoading(false)
        })

        audio.addEventListener('timeupdate', () => {
          if (!isSeeking) {
            setCurrentTime(audio.currentTime)
          }
        })

        setAudioElement(audio)

        await audio.play()
        setIsPlaying(true)
        globalAudioManager.currentAudioElement = audio
        globalAudioManager.currentAudioSetter = setIsPlaying
      } catch {
        toast.error('Error playing audio')
        setIsLoading(false)
      }
    } else {
      if (isPlaying) {
        audioElement.pause()
        setIsPlaying(false)
        if (globalAudioManager.currentAudioElement === audioElement) {
          globalAudioManager.currentAudioElement = null
          globalAudioManager.currentAudioSetter = null
        }
      } else {
        stopCurrentAudio()

        try {
          await audioElement.play()
          setIsPlaying(true)
          globalAudioManager.currentAudioElement = audioElement
          globalAudioManager.currentAudioSetter = setIsPlaying
        } catch {
          toast.error('Error playing audio')
        }
      }
    }
  }

  const handleSeekStart = () => {
    if (audioElement && !audioElement.paused) {
      setWasPlaying(true)
      audioElement.pause()
    } else {
      setWasPlaying(false)
    }
    setIsSeeking(true)
  }

  const handleSeekChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTime = parseFloat(e.target.value)
    setCurrentTime(newTime)
    if (audioElement) {
      audioElement.currentTime = newTime
    }
  }

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60)
    const seconds = Math.floor(time % 60)
    return `${minutes}:${seconds.toString().padStart(2, '0')}`
  }

  const handleDownload = async () => {
    try {
      const token = await getToken()
      const audioUrl = `/api/v1/dialer/audio?filename=${encodeURIComponent(recordingName)}&campaign_id=${campaignId}`

      const response = await fetch(audioUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      if (!response.ok) {
        throw new Error('Failed to download')
      }

      const blob = await response.blob()
      const url = URL.createObjectURL(blob)

      const link = document.createElement('a')
      link.href = url
      link.download = recordingName
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    } catch {
      toast.error('Error downloading audio')
    }
  }

  return (
    <div className="flex items-center space-x-2 min-w-[200px]">
      <Button
        variant="ghost"
        size="sm"
        onClick={handlePlayPause}
        className="h-8 w-8 p-0 flex-shrink-0"
        disabled={isLoading}
      >
        {isLoading ? (
          <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
        ) : isPlaying ? (
          <Pause className="h-4 w-4" />
        ) : (
          <Play className="h-4 w-4" />
        )}
      </Button>

      <span className="text-detail text-muted-foreground flex-shrink-0">{formatTime(currentTime)}</span>

      <input
        type="range"
        min="0"
        max={duration || 100}
        value={currentTime}
        onChange={handleSeekChange}
        onMouseDown={handleSeekStart}
        onMouseUp={handleSeekEnd}
        step={0.1}
        className="flex-1 h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
        style={{
          background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${(currentTime / (duration || 100)) * 100}%, #e5e7eb ${(currentTime / (duration || 100)) * 100}%, #e5e7eb 100%)`,
        }}
      />

      <span className="text-detail text-muted-foreground flex-shrink-0">{formatTime(duration)}</span>

      <Button
        variant="ghost"
        size="sm"
        onClick={handleDownload}
        className="h-8 w-8 p-0 flex-shrink-0"
      >
        <Download className="h-4 w-4" />
      </Button>
    </div>
  )
}

const detailsRenderer = (event: string, details: string, campaignId?: number) => {
  if (event === 'Recording Completed' && campaignId) {
    return <AudioPlayer recordingName={details} campaignId={campaignId} />
  }
  return details
}

export function DialerCallLogs() {
  const apiClient = useApiClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const [phoneNumber, setPhoneNumber] = useState(searchParams.get('phone') || '')
  const [data, setData] = useState<CallLogsData | null>(null)
  const [isSearching, setIsSearching] = useState(false)
  const initialSearchDone = useRef(false)

  const fetchCallLogs = useCallback(
    async (phone: string) => {
      if (!phone.trim()) return

      setIsSearching(true)
      try {
        const result = await apiClient<CallLogsData>(
          `/dialer/call-logs?phone=${encodeURIComponent(phone.trim())}`
        )
        setData(result)
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to fetch call logs')
      } finally {
        setIsSearching(false)
      }
    },
    [apiClient]
  )

  // Initial search from URL params
  useEffect(() => {
    const phone = searchParams.get('phone')
    if (phone && !initialSearchDone.current) {
      initialSearchDone.current = true
      setPhoneNumber(phone)
      fetchCallLogs(phone)
    }
  }, [searchParams, fetchCallLogs])

  const handleSearch = () => {
    if (!phoneNumber.trim()) {
      toast.error('Please enter a phone number')
      return
    }

    const newParams = new URLSearchParams(searchParams)
    newParams.set('phone', phoneNumber.trim())
    setSearchParams(newParams)
    fetchCallLogs(phoneNumber)
  }

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch()
    }
  }

  const hasSearched = !!data?.phoneNumber

  return (
    <div className="w-full h-full p-6 overflow-y-auto">
      <style>{sliderStyles}</style>

      {/* Header */}
      <div className="mb-6">
        <h1 className="text-heading font-semibold text-gray-900">Call Logs</h1>
        <p className="text-body text-muted-foreground mt-1">
          Search for detailed call information by phone number
        </p>
      </div>

      {/* Search Card */}
      <Card className="mb-6 border shadow-sm">
        <CardHeader>
          <CardTitle>Search Call Logs</CardTitle>
          <CardDescription>Enter a customer phone number to view all call log events</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <Input
                placeholder="Enter phone number (e.g. +12025550123)"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                onKeyPress={handleKeyPress}
                className="w-full"
              />
            </div>
            <Button onClick={handleSearch} disabled={isSearching}>
              {isSearching ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Searching...
                </>
              ) : (
                <>
                  <Search className="h-4 w-4 mr-2" />
                  Search
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Results Card */}
      {hasSearched && (
        <Card className="border shadow-sm">
          <CardHeader>
            <CardTitle>Results for {data.phoneNumber}</CardTitle>
            <CardDescription>
              Found {data.totalCalls} call{data.totalCalls !== 1 ? 's' : ''} with{' '}
              {data.callLogs.length} event{data.callLogs.length !== 1 ? 's' : ''}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isSearching ? (
              <div className="space-y-2">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : data.callLogs.length === 0 ? (
              <div className="text-center py-12">
                <AlertCircle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-heading font-medium mb-2">No Call Logs Found</h3>
                <p className="text-muted-foreground mb-4">
                  We couldn't find any call logs for the number {data.phoneNumber}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[180px]">Timestamp</TableHead>
                      <TableHead>Event</TableHead>
                      <TableHead>Details</TableHead>
                      <TableHead className="text-right">Campaign</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.callLogs.map((log) => (
                      <TableRow key={log.id}>
                        <TableCell className="font-medium">
                          {format(new Date(log.timestamp), 'MMM d, yyyy HH:mm:ss')}
                        </TableCell>
                        <TableCell className="font-medium">
                          <span
                            className={`capitalize ${
                              log.event.includes('Failed')
                                ? 'text-destructive'
                                : log.event.includes('Call Started')
                                  ? 'text-green-600'
                                  : ''
                            }`}
                          >
                            {log.event}
                          </span>
                        </TableCell>
                        <TableCell>{detailsRenderer(log.event, log.details, log.campaignId)}</TableCell>
                        <TableCell className="text-right text-muted-foreground">
                          {log.campaignName || 'N/A'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  )
}